"use client";
import { useMemo } from "react";
import Link from "next/link";
import { BellRing, ChevronLeft, Star } from "lucide-react";
import { useProductBase } from "@core/helpers/client/hooks/useProduct";
import useFixedBarHeight, { PAGE_FIXED_FOOTER_HEIGHT_VAR } from "@/hooks/useFixedBarHeight";
import Share from "@/components/Share";
import LIkeButtonProduct from "@/components/LikeButtonProduct";
import GuaranteeProduct from "@/components/guaranteeProduct";
import RelatedProducts from "@/components/RelatedProducts";
import OfferBox from "@/components/OfferBox";
import AddToCartMobile from "./_addToCartMobile";
import Variants from "./_variants";
import ProductGallery from "./_imageGallery";
import MainInfo from "./_mainInfo";
import AddToCartDesktop from "./_addToCartDecktop";
import { BrandLabel, BrandLabelDescription } from "./_brandLabel";
import ProductNotice from "./_productNotice";
import { ProductLabel } from "./_productLabel";
import { ProductLabelGuarantee } from './_productLabel';
import Tabs, { scroll_to_product_tab } from "./Tabs";
import Gateway from "./_gateway";
import PresaleLeadTime from "@/components/PresaleLeadTime";
import { with_presale_stock } from "@/helpers/presale";
import useHydrated from "@/hooks/useHydrated";
import DeviceOnly from "@/components/DeviceOnly";


export default function SingleProduct({ data = {}, widgets, model_name, model_type, initial_labels }) {

    /* واریانتِ پیش‌فروش موجودیِ صفر دارد و هسته آن را «ناموجود» می‌بیند؛
       `with_presale_stock` تعدادِ باقی‌مانده‌ی پیش‌فروش را جای موجودی می‌گذارد. */
    const product_data = useMemo(() => with_presale_stock(data), [data]);

    const {
        selected_mix_variant,
        check_product_availability,
        check_product_offer,
        select_variant_handler,
        selected_labels,
        available_variants,
        add_to_cart_handler,
        add_to_cart_loading,
        item_count_in_cart: item_count_in_cart_now,
        mix_variant_limit_min,
        mix_variant_limit_max,
        change_count_items_in_cart_handler,
        is_favorite,
        add_product_to_favorites_handler,
        add_to_favorite_loading,
        remove_from_cart_handler,
        remove_from_cart_loading,
        add_product_to_notifications_handler,
        is_notify,
        variants_with_disabled_unavailable
    } = useProductBase(product_data, undefined, undefined, undefined, { initial_labels });

    // سبد خرید در رندر سرور خالی است (AuthContext روی سرور سبد ندارد)؛ اگر همین محصول در سبد بازدیدکننده باشد،
    // دکمه‌ی «افزودن» سرور و شمارنده‌ی کلاینت فرق می‌کرد و hydration به هم می‌خورد. تا قبل از hydrate صفر،
    // بلافاصله بعد از آن تعداد واقعی.
    const hydrated = useHydrated();
    const item_count_in_cart = (...args) => (hydrated ? item_count_in_cart_now(...args) : 0);

    // مدل صفحه (model_name و model_type) و کلیدهای تنوعِ انتخاب‌شده همراه گزارش محصول به پنل می‌رود تا کارت همان تنوع
    // و لینک ویرایش همان نوع محصول را نشان بدهد
    const report_variant_keys = (selected_mix_variant?.keys || []).join(",");
    const report_model = { model_name, model_type, model_id: data?.id, variant_keys: report_variant_keys };

    const VISIBLE_VARIANT_KEYS = ["colors", "sizes"];
    const visible_variants = (variants_with_disabled_unavailable || []).filter(
        (variation) => VISIBLE_VARIANT_KEYS.includes(variation?.variant?.key)
    );

    const gallery =
        selected_mix_variant?.media_gallery?.length > 0
            ? selected_mix_variant.media_gallery
            : data?.media_gallery;


    const description_widget = widgets
        ?.find((item) => item?.section === "main")
        ?.widgets?.find((item) => item?.widget_component_key === "texteditor");

    /* بررسی تخصصی. هم‌کلیدِ معرفی محصول است (`texteditor`) و فقط `section`
       از هم جدایشان می‌کند. */
    const review_widget = widgets
        ?.find((item) => item?.section === "content")
        ?.widgets?.find((item) => item?.widget_component_key === "texteditor");


    const sizehint_widget = widgets
        ?.find((item) => item?.section === "size-hint")
        ?.widgets?.find((item) => item?.widget_component_key === "texteditor");

    const guarantee_widget = widgets
        ?.find((item) => item?.section === "guarantee")
        ?.widgets?.find((item) => item?.widget_component_key === "footer-guarantee-products");

    const handleScrolltTab = (id) => scroll_to_product_tab(id);
    const mobileBarRef = useFixedBarHeight(PAGE_FIXED_FOOTER_HEIGHT_VAR);

    return (
        <>
            <DeviceOnly on="mobile">
                <div className="block lg:hidden">

                    {/* gallery */}
                    {gallery?.length > 0 || selected_mix_variant?.details?.product_code ?
                        <ProductGallery
                            gallery={gallery}
                            add_to_favorite_loading={add_to_favorite_loading[data?.id]}
                            add_product_to_favorites_handler={add_product_to_favorites_handler}
                            is_favorite={is_favorite[data?.id]}
                            data={data?.title}
                        /> : ""}

                    <div className="w-full mx-auto px-4 md:px-10 space-y-2 mt-2">

                        {/* progressbar & offerlebel & timer*/}
                        <OfferBox
                            data={data}
                            selected_mix_variant={selected_mix_variant}
                            check_product_availability={check_product_availability}
                            check_product_offer={check_product_offer}
                        />

                        {/* category & actions */}
                        <div className="w-full flex items-center justify-between">

                            {data?.category && (
                                <div className="w-[70%]">
                                    <Link
                                        href={data?.category?.seo?.full_url || ""}
                                        className="font-bold text-[13px] lg:text-sm text-jmt-primary-800 hover:text-jmt-primary-900"
                                    >
                                        {" "} دسته‌بندی {" "}
                                        {data?.category?.title}
                                    </Link>
                                </div>
                            )}

                            <div className="w-[30%]">
                                <div className="flex items-center justify-end gap-3">
                                    <LIkeButtonProduct
                                        data={data}
                                        is_favorite={is_favorite[data?.id]}
                                        add_to_favorite_loading={add_to_favorite_loading?.[data?.id]}
                                        add_product_to_favorites_handler={add_product_to_favorites_handler}
                                        className="flex items-center justify-center w-8 h-8 bg-white border border-jmt-neutral-200 hover:border-jmt-neutral-300 rounded-full shrink-0"
                                    />
                                    {/* علامت نوتیفیکیشن فعلا لازم نیست
                                    <div className="flex items-center justify-center w-8 h-8 bg-white border border-jmt-neutral-200 hover:border-jmt-neutral-300 rounded-full shrink-0">
                                        <BellRing size={18} />
                                    </div>
                                    */}

                                    <Share className="flex items-center justify-center w-8 h-8 bg-white border border-jmt-neutral-200 hover:border-jmt-neutral-300 rounded-full shrink-0" />

                                </div>
                            </div>
                        </div>

                        {/* brands lebel */}
                        <div className="flex items-center gap-2">
                            {data?.brand?.title ? (
                                <Link
                                    href={data?.brand?.seo?.full_url || ""}
                                    className="font-bold text-xs lg:text-sm text-jmt-main hover:text-jmt-neutral-800"
                                >
                                    {data?.brand?.title}
                                </Link>
                            ) : ""}

                            <BrandLabel brand={data?.brand} />

                            {data?.custom_fields?.non_original_product &&
                                <>
                                    <div className="text-jmt-neutral-200">|</div>
                                    <ProductLabel noneOrginal={data?.custom_fields?.non_original_product} />
                                </>}
                        </div>

                        {/* title */}
                        {data?.title ?
                            <h1 className="font-bold text-jmt-main">
                                {data?.title}
                            </h1> : ""}

                        {/* commnet & question & rate */}
                        {(data?.comments_count > 0 || data?.forums_count > 0) ?
                            <div className="flex flex-wrap items-center gap-2 pb-3">

                                {/* RATING */}
                                {data?.comments_count > 0 ?
                                    <div className="flex items-center gap-1">
                                        <Star size={12} fill="#FACC15" stroke="none" />
                                        <span className="font-bold text-xs text-jmt-main">{data?.average_rate}</span>
                                    </div> : ""}

                                {/* comments */}
                                {data?.comments_count > 0 ?
                                    <button
                                        onClick={() => handleScrolltTab("comments")}
                                        className="flex items-center justify-center text-xs font-bold text-jmt-neutral-800 bg-jmt-neutral-100 p-1 gap-1 rounded-md hover:text-jmt-main cursor-pointer">
                                        <span> {data?.comments_count} نظر </span>
                                        <ChevronLeft size={16} className="text-jmt-neutral-800" />
                                    </button> : ""}

                                {/* questions */}
                                {data?.forums_count > 0 ?
                                    <button
                                        onClick={() => handleScrolltTab("questions")}
                                        className="flex items-center justify-center text-xs font-bold text-jmt-neutral-800 bg-jmt-neutral-100 p-1 gap-1 rounded-md hover:text-jmt-main cursor-pointer">
                                        <span> {data?.forums_count} پرسش </span>
                                        <ChevronLeft size={16} className="text-jmt-neutral-800" />
                                    </button> : ""}

                            </div>
                            : ""}

                        {/* variable */}
                        {visible_variants?.length > 0 && (
                            <>
                                {[...visible_variants]
                                    .sort((a, b) => {
                                        const aKey = a.variant?.key || "";
                                        const bKey = b.variant?.key || "";

                                        if (aKey === "colors" && bKey !== "colors") return -1;
                                        if (aKey !== "colors" && bKey === "colors") return 1;
                                        return 0;
                                    })
                                    .map((variation, variation_index) => (
                                        <Variants
                                            key={variation?.id ?? variation?.variant?.id ?? `variant-${variation_index}`}
                                            title={variation.variant?.name}
                                            attributes={variation.labels}
                                            selected_labels={selected_labels}
                                            type={variation.variant?.type}
                                            isSize={
                                                variation?.variant?.key === "sizes" &&
                                                sizehint_widget?.fields?.description?.length > 0
                                            }
                                            variant_id={variation.variant?.id}
                                            sizeHintDescription={sizehint_widget?.fields?.description}
                                            onClick={(label) =>
                                                select_variant_handler(
                                                    label?.id,
                                                    variation.variant?.id
                                                )
                                            }
                                        />
                                    ))}
                            </>
                        )}
                    </div>

                    <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2" />

                    {/* maininfo */}
                    <div className="w-full mx-auto px-4 md:px-10 py-5">
                        <MainInfo data={data} handleScrolltTab={handleScrolltTab} />
                    </div>

                    <div className="h-0.5 w-full bg-jmt-neutral-200" />


                    {/* ProductLabelGuarantee & Gateway */}
                    <div className="w-full mx-auto px-4 md:px-10 border-b-2 border-jmt-neutral-200 py-5">
                        <div className="space-y-2">
                            <ProductLabelGuarantee noneOrginal={data?.custom_fields?.non_original_product} />
                            {/* زمان آماده‌سازی - فقط پیش‌فروش */}
                            {selected_mix_variant?.presale?.available && (
                                <PresaleLeadTime
                                    hours={selected_mix_variant?.presale?.lead_hours}
                                    className="text-jmt-neutral-700 text-xs font-bold"
                                    textClassName="font-bold text-xs text-jmt-neutral-700"
                                    icon={
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M10 2H14M12 14L15 11M20 14C20 18.4183 16.4183 22 12 22C7.58172 22 4 18.4183 4 14C4 9.58172 7.58172 6 12 6C16.4183 6 20 9.58172 20 14Z" stroke="#171717" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    }
                                />
                            )}
                        </div>

                        {/* installment */}
                        <Gateway products={[{ price: selected_mix_variant?.price?.main_price, count: item_count_in_cart() || 1 }]} />
                    </div>

                    {data?.brand?.custom_fields?.custom_description &&
                        <>
                            {/* <BrandLabelDescription brand={data?.brand} /> */}
                            {/* <div className="h-0.5 w-full bg-jmt-neutral-200" /> */}
                        </>}

                    {/* ProductNotice */}
                    {selected_mix_variant?.details?.product_code ?
                        <div className="w-full mx-auto px-4 md:px-10 pb-5">
                            <ProductNotice
                                sku={selected_mix_variant?.details?.product_code}
                                noneOrginal={data?.custom_fields?.non_original_product}
                                category={data?.category}
                                contact={data?.price_contact_number}
                                report_model={report_model}
                            />
                        </div> : ""}

                    <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2" />

                    {/* tabs */}
                    <Tabs
                        variant="mobile"
                        description={description_widget?.fields?.description || ""}
                        review={review_widget?.fields?.description || ""}
                        information={data}
                        comments={data}
                        questions={data}
                    />

                    {/* RelatedProducts */}
                    {data?.related_products?.length > 0 ?
                        <>
                            <div className="h-0.5 w-full bg-jmt-neutral-200 mt-2" />
                            <div className="w-full mx-auto px-4 md:px-10 pt-5 mt-5">
                                <RelatedProducts related_products={data?.related_products} title={" کالاهای مشابه  "} />
                            </div>
                        </>
                        : ""}

                    {/* RelatedProducts */}
                    {data?.suggestion_products?.length > 0 ?
                        <div className="w-full mx-auto px-4 md:px-10">
                            <RelatedProducts related_products={data?.suggestion_products} title={" درکنارش خریداری شده "} />
                        </div>
                        : ""}

                    {/* ADD TO CART */}
                    <div
                        ref={mobileBarRef}
                        style={{ bottom: "var(--footer-nav-height, 0px)" }}
                        className="fixed left-0 right-0 w-full bg-jmt-white border-t border-jmt-neutral-200 z-30 px-4 py-3" >
                        <AddToCartMobile
                            data={data}
                            selected_mix_variant={selected_mix_variant}
                            check_product_availability={check_product_availability}
                            check_product_offer={check_product_offer}
                            add_to_cart_handler={add_to_cart_handler}
                            add_to_cart_loading={add_to_cart_loading}
                            item_count_in_cart={item_count_in_cart}
                            mix_variant_limit_min={mix_variant_limit_min}
                            mix_variant_limit_max={mix_variant_limit_max}
                            change_count_items_in_cart_handler={
                                change_count_items_in_cart_handler
                            }
                            remove_from_cart_handler={remove_from_cart_handler}
                            remove_from_cart_loading={remove_from_cart_loading}
                            add_product_to_notifications_handler={add_product_to_notifications_handler}
                            is_notify={is_notify}
                        />
                    </div >

                </div >
            </DeviceOnly>

            <DeviceOnly on="desktop">
                <div className="hidden lg:block">
                    <div className="container">
                        <div className="flex justify-between space-x-8 mb-10">

                            {/* gallery */}
                            {gallery?.length > 0 || selected_mix_variant?.details?.product_code ?
                                <div className="w-[32%]">
                                    <ProductGallery
                                        gallery={gallery}
                                        add_to_favorite_loading={add_to_favorite_loading[data?.id]}
                                        add_product_to_favorites_handler={add_product_to_favorites_handler}
                                        is_favorite={is_favorite[data?.id]}
                                        data={data?.title}
                                    />

                                    <ProductNotice
                                        sku={selected_mix_variant?.details?.product_code}
                                        noneOrginal={data?.custom_fields?.non_original_product}
                                        category={data?.category}
                                        contact={data?.price_contact_number}
                                        report_model={report_model}
                                    />
                                </div>
                                : ""}

                            {/* product info */}
                            <div className="w-[43%]">

                                <div className="flex flex-row items-center gap-2 pb-7.5">

                                    {/* brand info */}
                                    <div className="flex items-center gap-2">

                                        {data?.brand?.title ? (
                                            <Link
                                                href={data?.brand?.seo?.full_url || ""}
                                                className="font-bold text-xs lg:text-sm text-jmt-main hover:text-jmt-neutral-800"
                                            >
                                                {data?.brand?.title}
                                            </Link>
                                        ) : ""}

                                        {/* brand & product label */}
                                        <div className="flex items-center gap-2">
                                            <BrandLabel brand={data?.brand} />

                                            {data?.custom_fields?.non_original_product &&
                                                <>
                                                    <div className="text-jmt-neutral-200">|</div>
                                                    <ProductLabel noneOrginal={data?.custom_fields?.non_original_product} />
                                                </>}
                                        </div>
                                    </div>

                                    {data?.category && (
                                        <div className="flex items-center gap-2">
                                            <div className="block text-jmt-neutral-200">|</div>

                                            <Link
                                                href={data?.category?.seo?.full_url || ""}
                                                className="font-bold text-xs lg:text-sm text-jmt-primary-800 hover:text-jmt-primary-900"
                                            >
                                                {" "} دسته‌بندی {" "}
                                                {data?.category?.title}
                                            </Link>
                                        </div>
                                    )}
                                </div>

                                {/* title: h1 مثل نسخه‌ی موبایل؛ DeviceOnly فقط یکی از دو نسخه را رندر می‌کند (قبلاً دسکتاپ h1 نداشت) */}
                                {data?.title ?
                                    <h1 className="text-lg font-bold text-jmt-main pb-3">
                                        {data?.title}
                                    </h1> : ""}

                                {/* commnet & question & rate */}
                                {(data?.comments_count > 0 || data?.forums_count > 0) ?
                                    <div className="flex flex-wrap items-center gap-2 pb-6">

                                        {/* RATING */}
                                        {data?.comments_count > 0 ?
                                            <div className="flex items-center gap-1">
                                                <Star size={12} fill="#FACC15" stroke="none" />
                                                <span className="font-bold text-xs text-jmt-main">{data?.average_rate}</span>
                                            </div> : ""}

                                        {/* comments */}
                                        {data?.comments_count > 0 ?
                                            <button
                                                onClick={() => handleScrolltTab("comments")}
                                                className="flex items-center justify-center text-xs font-bold text-jmt-neutral-800 bg-jmt-neutral-100 p-1 gap-1 rounded-md hover:text-jmt-main cursor-pointer">
                                                <span> {data?.comments_count} نظر </span>
                                                <ChevronLeft size={16} className="text-jmt-neutral-800" />
                                            </button> : ""}

                                        {/* questions */}
                                        {data?.forums_count > 0 ?
                                            <button
                                                onClick={() => handleScrolltTab("questions")}
                                                className="flex items-center justify-center text-xs font-bold text-jmt-neutral-800 bg-jmt-neutral-100 p-1 gap-1 rounded-md hover:text-jmt-main cursor-pointer">
                                                <span> {data?.forums_count} پرسش </span>
                                                <ChevronLeft size={16} className="text-jmt-neutral-800" />
                                            </button> : ""}

                                    </div>
                                    : ""}

                                {/* variable */}
                                {visible_variants?.length > 0 && (
                                    <>
                                        {[...visible_variants]
                                            .sort((a, b) => {
                                                const aKey = a.variant?.key || "";
                                                const bKey = b.variant?.key || "";

                                                if (aKey === "colors" && bKey !== "colors") return -1;
                                                if (aKey !== "colors" && bKey === "colors") return 1;
                                                return 0;
                                            })
                                            .map((variation, variation_index) => (
                                                <Variants
                                                    key={variation?.id ?? variation?.variant?.id ?? `variant-${variation_index}`}
                                                    title={variation.variant?.name}
                                                    attributes={variation.labels}
                                                    selected_labels={selected_labels}
                                                    type={variation.variant?.type}
                                                    isSize={
                                                        variation?.variant?.key === "sizes" &&
                                                        sizehint_widget?.fields?.description?.length > 0
                                                    }
                                                    variant_id={variation.variant?.id}
                                                    sizeHintDescription={sizehint_widget?.fields?.description}
                                                    onClick={(label) =>
                                                        select_variant_handler(
                                                            label?.id,
                                                            variation.variant?.id
                                                        )
                                                    }
                                                />
                                            ))}
                                    </>
                                )}

                                <MainInfo data={data} handleScrolltTab={handleScrolltTab} />

                                {/* <BrandLabelDescription brand={data?.brand} /> */}
                            </div>

                            {/* ADD TO CART */}
                            <div className="w-[25%] pb-8">
                                <div className="sticky top-[calc(var(--header-height)+12px)]">

                                    <AddToCartDesktop
                                        data={data}
                                        showDetails={false}
                                        selected_mix_variant={selected_mix_variant}
                                        check_product_availability={check_product_availability}
                                        check_product_offer={check_product_offer}
                                        add_to_cart_handler={add_to_cart_handler}
                                        add_to_cart_loading={add_to_cart_loading}
                                        item_count_in_cart={item_count_in_cart}
                                        mix_variant_limit_min={mix_variant_limit_min}
                                        mix_variant_limit_max={mix_variant_limit_max}
                                        change_count_items_in_cart_handler={
                                            change_count_items_in_cart_handler
                                        }
                                        remove_from_cart_handler={remove_from_cart_handler}
                                        remove_from_cart_loading={remove_from_cart_loading}
                                        add_product_to_notifications_handler={add_product_to_notifications_handler}
                                        is_notify={is_notify}
                                        noneOrginal={data?.custom_fields?.non_original_product}
                                    />

                                </div>
                            </div>

                        </div>

                        {/* GuaranteeProduct */}
                        {guarantee_widget ?
                            <div className="w-full mb-10">
                                <GuaranteeProduct guarantee_widget={guarantee_widget} className="row-3 border-y-2 border-jmt-neutral-200 text-jmt-main gap-x-6 py-6" />
                            </div>
                            : ""}

                        {/* RelatedProducts */}
                        {data?.related_products?.length > 0 ?
                            <div className="w-full mb-10">
                                <RelatedProducts related_products={data?.related_products} title={" کالاهای مشابه  "} />
                            </div>
                            : ""}

                        <div className="flex justify-between space-x-8 mb-10">

                            {/* TABS */}
                            <div className="w-[75%]">
                                <Tabs
                                    variant="desktop"
                                    description={description_widget?.fields?.description || ""}
                                    review={review_widget?.fields?.description || ""}
                                    information={data}
                                    comments={data}
                                    questions={data}
                                />

                            </div>

                            {/* ADD TO CART */}
                            <div className="w-[25%]">
                                <div className="sticky top-[calc(var(--header-height)+12px)]">
                                    <AddToCartDesktop
                                        data={data}
                                        selected_mix_variant={selected_mix_variant}
                                        check_product_availability={check_product_availability}
                                        check_product_offer={check_product_offer}
                                        add_to_cart_handler={add_to_cart_handler}
                                        add_to_cart_loading={add_to_cart_loading}
                                        item_count_in_cart={item_count_in_cart}
                                        mix_variant_limit_min={mix_variant_limit_min}
                                        mix_variant_limit_max={mix_variant_limit_max}
                                        change_count_items_in_cart_handler={
                                            change_count_items_in_cart_handler
                                        }
                                        remove_from_cart_handler={remove_from_cart_handler}
                                        remove_from_cart_loading={remove_from_cart_loading}
                                        add_product_to_notifications_handler={add_product_to_notifications_handler}
                                        is_notify={is_notify}
                                    />

                                </div>
                            </div>
                        </div>

                        {/* RelatedProducts */}
                        {data?.suggestion_products?.length > 0 ?
                            <div className="w-full space-y-6">
                                <div className="w-full h-0.5 bg-jmt-neutral-200" />
                                <RelatedProducts related_products={data?.suggestion_products} title={" درکنارش خریداری شده "} />
                            </div>
                            : ""}
                    </div>

                </div>
            </DeviceOnly>

        </>
    );
}
